import { Button, Section } from "@arp/react-ndbx";
import { Typography } from "@mui/material";
import React, { useEffect } from "react";
import Page from "../../../components/Page";
import PageLoadingBackdrop from "../../../components/organisms/PageLoadingBackdrop";
import { RetrieveOnlineButtonStack } from "../../../components/organisms/RetrieveOnlineButtonStack";
import { Application } from "../../../config/const/enums";
import {
  retrieveCommonDisclaimer,
  retrieveDeclineDisclaimer,
  policyOutcomeDisclaimers,
} from "../../../config/disclaimers/disclaimers";
import { useGetQuote } from "../../../hooks/dataHooks/useGetQuoteData";
import useNewBusiness from "../../../hooks/useNewBusiness";
import usePreQualification from "../../../hooks/usePreQualification";
import usePreQualificationPages from "../../../hooks/usePreQualificationPages";
import {
  getQuoteStatusMapping,
  matchPPStrings,
  matchReferralCode,
} from "../../../utilities/preQualificationUtils";
import { backToQuotePoint } from "../../../utilities/utils";
import { trackifyPageChange } from "@allianz/tracking";
import NotificationCard from "../../../components/coding/notificationCard/NotificationCard.jsx";

function RetrieveOutcomeDecline() {
  const { state } = usePreQualificationPages();
  const { currentPage } = { ...state };
  const { dataSet } = usePreQualification();
  const { personOne, application } = { ...dataSet };
  const { data: quoteData } = useGetQuote();
  const { data: newBusinessState } = useNewBusiness();

  useEffect(() => {
    trackifyPageChange(currentPage?.identifier?.toLowerCase());
  }, []);

  return (
    <Page title={currentPage.title} identifier={currentPage.identifier}>
      <PageLoadingBackdrop />
      <Section md={7} mb={4} mt={{ xs: 10, md: 15 }}>
        <Section mb={3}>
          <Typography variant="h2">
            {retrieveCommonDisclaimer.SORRY_DISCLAIMER}
          </Typography>
        </Section>
        <Section>
          <Typography variant="label-large">
            {retrieveCommonDisclaimer.QUOTE_REFERENCE}{" "}
          </Typography>
          <Typography variant="label-large-bold">
            {quoteData?.quoteInformation?.quoteNumber}
          </Typography>
        </Section>
        {state.application === Application.CONTACT_CENTER ? (
          <Section>
            <NotificationCard
              cardType="info"
              title="Applicant Privacy Check"
              message={policyOutcomeDisclaimers({
                isOnline: false,
              }).policyOutcomeInfo[0].DECLINED_APPLICANT_PRIVACY_CHECK(
                dataSet["personOne"].emailAddress,
                dataSet["personOne"].contactNumber
              )}
            />
          </Section>
        ) : null}
        <Section sx={{ mt: 5, mb: { xs: 3, sm: 4 } }}>
          <Section sx={{ maxWidth: { xs: 334 }, mb: 0 }}>
            <img
              width="100%"
              src="https://www.allianzretireplus.com.au/content/dam/onemarketing/azau/allianz-life-dam/images/woman_laptop_headset.svg"
            ></img>
          </Section>
        </Section>
        <Section mb={3}>
          <Typography variant="h5">
            Thank you {personOne?.firstName} for your application.
          </Typography>
        </Section>
        <Section mb={3} sx={{ textAlign: "left" }}>
          <Typography variant="p3">
            {retrieveDeclineDisclaimer.UNABLE_TO_COVER}
          </Typography>
        </Section>
        {/* loop the lifecover exclusions */}
        {newBusinessState?.covers?.life?.declineReasonList?.map(
          (declineReason, index) => (
            <Section mb={0} mt={0} sx={{ textAlign: "left" }}>
              <Typography variant="p3-bold">
                • Life cover: {declineReason}
              </Typography>
            </Section>
          )
        )}
        {/* loop the critical illness exclusions */}
        {newBusinessState?.covers?.criticalIllness?.declineReasonList?.map(
          (declineReason, index) => (
            <Section mb={0} mt={0} sx={{ textAlign: "left" }}>
              <Typography variant="p3-bold">
                • Critical Illness: {declineReason}
              </Typography>
            </Section>
          )
        )}
        {/* loop the unable to work exclusions */}
        {newBusinessState?.covers?.unableToWork?.declineReasonList?.map(
          (declineReason, index) => (
            <Section mb={0} mt={0} sx={{ textAlign: "left" }}>
              <Typography variant="p3-bold">
                • Unable To Work: {declineReason}
              </Typography>
            </Section>
          )
        )}
        <Section mt={3} mb={3} sx={{ textAlign: "left" }}>
          <Typography variant="p3">
            {retrieveDeclineDisclaimer.DECLINE_DUTY_DISCLAIMER}
          </Typography>
        </Section>
        <Section mb={3} sx={{ textAlign: "left" }}>
          <Typography variant="p3">
            {retrieveDeclineDisclaimer.MEDICAL_DISCLAIMER}
          </Typography>
        </Section>
        <Section mb={5} sx={{ textAlign: "left" }}>
          <Typography variant="p3">
            {retrieveDeclineDisclaimer.UNABLE_TO_ASSIST}
          </Typography>
        </Section>
        {getQuoteStatusMapping(newBusinessState?.policyState) === "NBR" && (
          <>
            <Section mb={3} sx={{ textAlign: "left" }}>
              <Typography variant="label-large-bold">
                {matchReferralCode(newBusinessState?.covers)} :{" "}
                {retrieveDeclineDisclaimer.OTHER_MEDICAL_INFO}
              </Typography>
            </Section>
            <Section sx={{ textAlign: "left" }}>
              <Typography variant="p3">
                {retrieveDeclineDisclaimer.UNDERWRITER_DISCLAIMER}
              </Typography>
            </Section>
          </>
        )}
      </Section>
      {state.application === Application.CONTACT_CENTER ? (
        <Section mt={9} md={4} mb={12}>
          <Button
            id="cc-processing-continue"
            ariaLabel="continue"
            variant="contained"
            color="primary"
            size="medium"
            onClick={() => backToQuotePoint(Application.CONTACT_CENTER)}
            type="button"
          >
            {retrieveCommonDisclaimer.CLOSE}
          </Button>
        </Section>
      ) : (
        <Section md={10} mt={9} mb={12}>
          <RetrieveOnlineButtonStack
            backToQuotePoint={backToQuotePoint}
            showCallButton={
              matchPPStrings(newBusinessState.auraStatus) ? false : true
            }
          />
        </Section>
      )}
    </Page>
  );
}

export default RetrieveOutcomeDecline;
